import {
  HiringProcessAssigneeRole,
  HiringProcessItemType,
} from "@api/shared-runtime/enums";

/** Item types that show an "Instructions" rich-text field in the editor. */
export const RICH_TEXT_ITEM_TYPES: HiringProcessItemType[] = [
  HiringProcessItemType.DocumentShare,
  HiringProcessItemType.DocumentUpload,
];

/** Item types that show template-level files/links in the editor. */
export const TEMPLATE_CONTENT_ITEM_TYPES: HiringProcessItemType[] = [
  HiringProcessItemType.DocumentShare,
];

// Subtle left-border colors for stage visual hierarchy
export const STAGE_COLORS = [
  "border-l-blue-400",
  "border-l-violet-400",
  "border-l-emerald-400",
  "border-l-amber-400",
  "border-l-rose-400",
  "border-l-cyan-400",
  "border-l-orange-400",
  "border-l-teal-400",
];

export const STAGE_PILL_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-violet-100 text-violet-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-cyan-100 text-cyan-700",
  "bg-orange-100 text-orange-700",
  "bg-teal-100 text-teal-700",
];

export function getItemTypeBadgeClass(itemType: HiringProcessItemType): string {
  switch (itemType) {
    case HiringProcessItemType.Form:
      return "bg-blue-50 text-blue-700 border-blue-200";
    case HiringProcessItemType.DocumentShare:
      return "bg-indigo-50 text-indigo-700 border-indigo-200";
    case HiringProcessItemType.DocumentUpload:
      return "bg-amber-50 text-amber-700 border-amber-200";
    case HiringProcessItemType.DocumentSigning:
      return "bg-green-50 text-green-700 border-green-200";
    default:
      return "bg-gray-50 text-gray-600 border-gray-200";
  }
}

/**
 * One-word badge text. Deliberately distinct words (To-do / Form / Shared /
 * Upload / PDF) so types never read alike at a glance. See
 * HIRING_PROCESS_ITEM_TYPES.md.
 */
export function getItemTypeBadgeText(itemType: HiringProcessItemType): string {
  switch (itemType) {
    case HiringProcessItemType.Form:
      return "Form";
    case HiringProcessItemType.DocumentShare:
      return "Shared";
    case HiringProcessItemType.DocumentUpload:
      return "Upload";
    case HiringProcessItemType.DocumentSigning:
      return "PDF";
    default:
      return "To-do";
  }
}

/**
 * Full, action-first label for an item. `document_upload` and
 * `document_signing` mean opposite things depending on the owner, so when the
 * assignee role is known we make the direction explicit. Without a role we fall
 * back to a neutral concept name (used in type-change diffs, etc.).
 */
export function getItemTypeLabel(
  itemType: HiringProcessItemType,
  assigneeRole?: HiringProcessAssigneeRole,
): string {
  switch (itemType) {
    case HiringProcessItemType.Form:
      return "Form to fill out";
    case HiringProcessItemType.DocumentShare:
      return "Shared file or link";
    case HiringProcessItemType.DocumentUpload:
      if (assigneeRole === undefined) {
        return "File request";
      }
      return "Request a file from the provider";
    case HiringProcessItemType.DocumentSigning:
      return "PDF to complete";
    default:
      return "To-do";
  }
}

export function getAssigneeBadgeClass(role: HiringProcessAssigneeRole): string {
  return role === HiringProcessAssigneeRole.Provider
    ? "bg-purple-50 text-purple-700 border-purple-200"
    : "bg-green-50 text-green-700 border-green-200";
}
